// externals
import { useSelector } from 'react-redux';

// libraries
import { CheckCircleIcon } from '@interstate/components/Icons';
import { PlusCircleIcon } from '@interstate/components/Icons/PlusCircleIcon';
import { Typography } from '@interstate/components/Typography';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

// hooks
import { dealLockHooks } from '../../hooks';

// components
import CashDownField from './CashDownField.interstate';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

// styles
import { StyledDealerDiscount, StyledDeferredAmountText } from './Terms.style';
import { deferredDownPaymentContainerStyle } from '../../dealSummary/common/ComponentLabelStyle';

// consts/enums
import { generateCashDownLabel } from './utils';

type CashDownFieldWithDefermentProps = {
    toggleSlideOut?: () => void;
};

const DEFERMENT = 'Deferment';
const DEFERRED = 'Deferred';

export const CashDownFieldWithDeferment = ({ toggleSlideOut }: CashDownFieldWithDefermentProps) => {
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const deductDueAtSigning = useSelector(dealerSelectors.getIsDueAtSigninAmountDeduct);
    const isTargetOutOfPocket = useSelector(offerReduxSelectors.getIsTargetOutOfPocket);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const isEnhancedDealDetailsEnabled = useSelector(featureToggleSelectors.getEnhancedDealDetails);
    const isTargetOutOfPocketLeaseEnabled = useSelector(featureToggleSelectors.isTargetOutOfPocketLeaseEnabled);
    const isDueAtSigningEnhancementsEnabled = useSelector(featureToggleSelectors.enableDueAtSigningEnhancements);
    const isLocked = dealLockHooks.useIsDealLocked();
    const deferredTotalAmount = useSelector(offerReduxSelectors.getDeferredDownPayment)?.amount;
    const label = generateCashDownLabel(
        offerType,
        isOnePayLease ? false : deductDueAtSigning,
        isTargetOutOfPocket,
        isEnhancedDealDetailsEnabled,
        isTargetOutOfPocketLeaseEnabled,
        isDueAtSigningEnhancementsEnabled
    );

    const renderDefermentApplied = () => {
        return (
            <div style={deferredDownPaymentContainerStyle} data-testid="deferred-amount-label">
                <StyledDeferredAmountText>
                    <CheckCircleIcon
                        aria-label="Deferred Amount Applied"
                        color="#07bc0c"
                        style={{ marginRight: '0.25rem', display: 'inline-flex' }}
                    />
                    <Typography variant={'body-xs'}>
                        {DEFERRED}: {formatDollarsAndCents(deferredTotalAmount)}
                    </Typography>
                </StyledDeferredAmountText>
            </div>
        );
    };

    return (
        <>
            <div style={deferredDownPaymentContainerStyle} data-testid="cash-down-label">
                <Typography variant={'body-xs'} color="#6d6d6d" sx={{ cursor: 'default', userSelect: 'none' }}>
                    {label}
                </Typography>
                <StyledDealerDiscount
                    data-testid="deferment-action"
                    onClick={toggleSlideOut}
                    component="button"
                    {...(isLocked && { disabled: true, className: 'disabled-button' })}
                >
                    <PlusCircleIcon aria-label="Deferment" />
                    <Typography variant={'body-xs'}>{DEFERMENT}</Typography>
                </StyledDealerDiscount>
            </div>
            <CashDownField displayLabel={false} />

            {deferredTotalAmount !== undefined && deferredTotalAmount > 0 && renderDefermentApplied()}
        </>
    );
};

export default CashDownFieldWithDeferment;
